import React, { useEffect } from 'react';
import { AlertCircle, Loader2, Trash2, X } from 'lucide-react';
import { formatQuoteTotal } from '../../utils/rfqQuoteTotal';
import './RfqQuoteDeleteConfirmModal.css';

export type RfqQuoteDeleteConfirmTarget = {
  quoteLabel: string;
  vendorName?: string;
  amount?: number | null;
};

export type RfqQuoteDeleteConfirmModalProps = {
  open: boolean;
  target: RfqQuoteDeleteConfirmTarget | null;
  busy?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: () => void;
};

const RfqQuoteDeleteConfirmModal: React.FC<RfqQuoteDeleteConfirmModalProps> = ({
  open,
  target,
  busy = false,
  error = null,
  onClose,
  onConfirm,
}) => {
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, busy, onClose]);

  if (!open || !target) return null;

  const amountLabel =
    target.amount != null && Number.isFinite(target.amount)
      ? formatQuoteTotal(target.amount)
      : null;

  return (
    <div
      className="ops-modal-overlay rqdc-overlay"
      role="presentation"
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="ops-modal rqdc-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="rqdc-title"
        aria-describedby="rqdc-desc"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ops-modal-header rqdc-header">
          <div className="rqdc-title-block">
            <span className="rqdc-title-icon" aria-hidden>
              <Trash2 size={18} />
            </span>
            <h3 id="rqdc-title">Delete quote?</h3>
          </div>
          <button
            type="button"
            className="ops-modal-close"
            aria-label="Close"
            disabled={busy}
            onClick={onClose}
          >
            <X size={16} aria-hidden />
          </button>
        </header>

        <div className="ops-modal-body rqdc-body">
          <p id="rqdc-desc" className="rqdc-lead">
            This permanently removes the uploaded file from this deal. This cannot be undone.
          </p>

          <section className="rqdc-summary" aria-label="Quote to delete">
            {target.vendorName && <span className="rqdc-vendor">{target.vendorName}</span>}
            {amountLabel && <span className="rqdc-amount">{amountLabel}</span>}
            <span className="rqdc-label-badge">{target.quoteLabel}</span>
          </section>

          {error && (
            <p className="rqdc-error" role="alert">
              <AlertCircle size={14} aria-hidden /> {error}
            </p>
          )}
        </div>

        <footer className="ops-modal-footer rqdc-footer">
          <button type="button" className="ops-hide-completed-btn" disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="rqdc-confirm"
            disabled={busy}
            onClick={() => void onConfirm()}
          >
            {busy ? (
              <>
                <Loader2 size={14} className="rqdc-spin" aria-hidden /> Deleting…
              </>
            ) : (
              <>
                <Trash2 size={13} aria-hidden /> Delete quote
              </>
            )}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default RfqQuoteDeleteConfirmModal;
